import { ActivitySummaryMetric, TrackingCount, YearAndAllTimeCount } from './types';

/**
 * What a dual-value tile shows, per the LLD display rules (cce-4530):
 * - this year above 0 and different from all time: big this-year value, "in {year}", and an
 *   "{allTime} all time" line
 * - this year equal to all time: big value and "in {year}" only
 * - this year 0 with prior-year activity: all-time value only, no label and no second line
 * - both 0: "0 in {year}"
 */
export interface DualValueDisplay {
  primary: number;
  year?: number;
  allTime?: number;
}

export function getDualValueDisplay(value: YearAndAllTimeCount, year: number): DualValueDisplay {
  const { thisYear, allTime } = value;
  if (thisYear === 0 && allTime > 0) return { primary: allTime };
  if (thisYear === allTime) return { primary: thisYear, year };
  return { primary: thisYear, year, allTime };
}

/**
 * Rounds down so 100% only ever means every completed shift is tracked
 * (OQ-11). Returns undefined at 0 of 0, which hides the tile (OQ-3).
 */
export function getTrackingRatePercent(count: TrackingCount): number | undefined {
  if (count.total <= 0) return undefined;
  const percent = Math.floor((count.tracked * 100) / count.total);
  return Math.min(100, Math.max(0, percent));
}

/** Minutes are summed on the server; hours are converted once here so rounding lives in one place. */
export function minutesToHours(minutes: number): number {
  return minutes / 60;
}

/** At most one decimal place, with ".0" dropped (OQ-8): 12, 1.5. */
export function formatHours(hours: number, locale: string): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(hours);
}

export function formatCount(count: number, locale: string): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(count);
}

/** Year of a timestamp in the caller's time zone and locale, so a Jan 1 UTC start can't show the wrong year. */
export function formatYearInTimeZone(date: Date, timeZone: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { year: 'numeric', timeZone }).format(date);
}

/**
 * A metric is shown only when its field is present and it isn't listed as unavailable. An absent
 * field that isn't listed is legitimately hidden; a listed one failed to load. Both hide the tile.
 */
export function isMetricAvailable<T>(
  value: T | undefined,
  metric: ActivitySummaryMetric,
  unavailableMetrics: ActivitySummaryMetric[]
): value is T {
  return value !== undefined && !unavailableMetrics.includes(metric);
}
